/**
 * Dining Table & Chestnut Sawbuck Table — chair bundle add-to-cart.
 * Adds table variant + N× Windsor chair, then applies set discount via Cart API.
 *
 * Fix (Oct 2026): the theme replaces the contents of #price-<section> on every
 * variant change, so price elements are now looked up fresh on every render
 * (never cached), stale async results are discarded, and the bundle price is
 * re-applied whenever the price block is re-rendered.
 */
(function () {
  const CHAIR_VARIANTS = {
    cherry: '50443896684839',
    walnut: '50443896783143',
  };

  const DISCOUNT_PREFIX = {
    dining: 'DiningTableBundle',
    sawbuck: 'ChestnutSawbuckBundle',
  };

  const NO_BUNDLE = 'No bundle selected';
  const priceCache = new Map();

  function parseBundleOption(value) {
    if (!value || value === NO_BUNDLE) return null;
    const match = value.match(/^(Two|Four|Six|Eight) (Cherry|Walnut) Windsor Chairs/i);
    if (!match) return null;

    const qtyMap = { two: 2, four: 4, six: 6, eight: 8 };
    const percentMap = { 2: 6, 4: 8, 6: 10, 8: 12 };
    const quantity = qtyMap[match[1].toLowerCase()];
    const wood = match[2].toLowerCase();

    return { quantity, wood, percent: percentMap[quantity] };
  }

  function formatMoney(cents, currency, withCode) {
    const iso = currency || 'USD';
    const formatted = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: iso,
    }).format(cents / 100);
    return withCode ? `${formatted} ${iso}` : formatted;
  }

  function fetchVariantPrice(variantId) {
    const key = String(variantId);
    if (!priceCache.has(key)) {
      const request = fetch(`/variants/${key}.js`)
        .then((response) => {
          if (!response.ok) throw new Error('Failed to load variant price');
          return response.json();
        })
        .then((variant) => variant.price)
        .catch((error) => {
          priceCache.delete(key);
          throw error;
        });
      priceCache.set(key, request);
    }
    return priceCache.get(key);
  }

  function initSection(root) {
    const bundleType = root.dataset.diningBundle;
    if (!bundleType || !DISCOUNT_PREFIX[bundleType]) return;

    const sectionId = root.dataset.section;
    const currency = window.Shopify?.currency?.active || 'USD';

    const addonSelect = root.querySelector('select[name="Add-Ons"]');
    const quantityForm = root.querySelector(`#Quantity-Form-${sectionId}`);
    const variantIdInput = root.querySelector(`#product-form-${sectionId} input[name="id"]`);

    if (!addonSelect || !variantIdInput) return;

    let renderToken = 0;

    // Always query fresh: the theme swaps out the price block on variant change.
    function priceEls() {
      const container = document.getElementById(`price-${sectionId}`) || root;
      return {
        container,
        defaultPrice: container.querySelector('.price.price--large:not(.bundle_price)'),
        bundleWrap: container.querySelector('.bundle_price'),
        bundleCompare: container.querySelector('.bundle_price__compare'),
        bundleSale: container.querySelector('.bundle_price__sale'),
      };
    }

    function buttonEls() {
      return {
        defaultSubmit: root.querySelector('button.product-form__submit'),
        customWrap: root.querySelector('.custom_add-to-cart'),
        customBtn: root.querySelector('.cus_add-to-cart'),
      };
    }

    function showsCurrencyCode(defaultPrice) {
      return Boolean(defaultPrice && /\b[A-Z]{3}\b/.test(defaultPrice.textContent || ''));
    }

    function getTableVariantId() {
      return String(variantIdInput.value || '');
    }

    function getBundleSelection() {
      return parseBundleOption(addonSelect.value);
    }

    function getDiscountCode(quantity) {
      return `${DISCOUNT_PREFIX[bundleType]}${quantity}`;
    }

    function getTableQuantity() {
      const qtyInput = quantityForm?.querySelector('input[name="quantity"]');
      const qty = parseInt(qtyInput?.value, 10);
      return Number.isFinite(qty) && qty > 0 ? qty : 1;
    }

    // custom.css hides .bundle_price.price.price--large, so visibility is set inline.
    function setBundleVisible(bundleWrap, visible) {
      if (!bundleWrap) return;
      bundleWrap.hidden = !visible;
      bundleWrap.style.display = visible ? 'block' : 'none';
    }

    // Sticky add-to-cart bar price (#sticky_add_tocat .product-price).
    function setStickyPrice(text) {
      document.querySelectorAll('#sticky_add_tocat .product-price').forEach((el) => {
        if (!el.dataset.tablePrice) el.dataset.tablePrice = el.textContent.trim();
        el.textContent = text || el.dataset.tablePrice;
      });
    }

    function tablePriceText(defaultPrice) {
      const item = defaultPrice && defaultPrice.querySelector('.price-item--last, .price-item--regular');
      return item ? item.textContent.trim() : '';
    }

    function showDefaultPrice() {
      const { defaultPrice, bundleWrap } = priceEls();
      if (defaultPrice) defaultPrice.style.display = '';
      setBundleVisible(bundleWrap, false);
      document.querySelectorAll('#sticky_add_tocat .product-price').forEach((el) => {
        el.dataset.tablePrice = '';
      });
      setStickyPrice(tablePriceText(defaultPrice));
    }

    function toggleButtons(bundleSelected) {
      const { defaultSubmit, customWrap } = buttonEls();
      if (defaultSubmit) defaultSubmit.style.display = bundleSelected ? 'none' : '';
      if (customWrap) {
        customWrap.classList.toggle('is-visible', bundleSelected);
        customWrap.style.display = bundleSelected ? 'block' : '';
      }
    }

    async function refreshBundlePrice() {
      const token = ++renderToken;
      const bundle = getBundleSelection();

      if (!bundle) {
        showDefaultPrice();
        toggleButtons(false);
        return;
      }

      toggleButtons(true);

      const tableVariantId = getTableVariantId();
      const chairVariantId = CHAIR_VARIANTS[bundle.wood];
      if (!tableVariantId || !chairVariantId) {
        showDefaultPrice();
        return;
      }

      try {
        const [tablePrice, chairUnitPrice] = await Promise.all([
          fetchVariantPrice(tableVariantId),
          fetchVariantPrice(chairVariantId),
        ]);

        // A newer selection happened while we were waiting — drop this result.
        if (token !== renderToken) return;
        if (String(variantIdInput.value) !== tableVariantId) return;

        const tableQty = getTableQuantity();
        const chairQty = bundle.quantity * tableQty;
        const subtotal = tablePrice * tableQty + chairUnitPrice * chairQty;
        const discounted = Math.round(subtotal * (1 - bundle.percent / 100));

        const { defaultPrice, bundleWrap, bundleCompare, bundleSale } = priceEls();
        const withCode = showsCurrencyCode(defaultPrice);

        if (bundleCompare) bundleCompare.textContent = formatMoney(subtotal, currency, withCode);
        if (bundleSale) bundleSale.textContent = formatMoney(discounted, currency, withCode);
        setBundleVisible(bundleWrap, true);
        if (defaultPrice) defaultPrice.style.display = 'none';
        setStickyPrice(formatMoney(discounted, currency, withCode));
      } catch (e) {
        if (token !== renderToken) return;
        const { bundleCompare, bundleSale, bundleWrap, defaultPrice } = priceEls();
        if (bundleCompare) bundleCompare.textContent = '';
        if (bundleSale) bundleSale.textContent = 'Unable to calculate';
        setBundleVisible(bundleWrap, true);
        if (defaultPrice) defaultPrice.style.display = 'none';
      }
    }

    let scheduled = null;
    function scheduleRefresh() {
      clearTimeout(scheduled);
      scheduled = setTimeout(refreshBundlePrice, 30);
    }

    async function addBundleToCart() {
      const bundle = getBundleSelection();
      if (!bundle) return;

      const tableVariantId = getTableVariantId();
      const chairVariantId = CHAIR_VARIANTS[bundle.wood];
      if (!tableVariantId || !chairVariantId) return;

      const { customBtn } = buttonEls();
      if (customBtn) {
        customBtn.disabled = true;
        customBtn.textContent = 'Adding...';
      }

      const tableQty = getTableQuantity();
      const chairQty = bundle.quantity * tableQty;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            items: [
              { id: Number(tableVariantId), quantity: tableQty },
              { id: Number(chairVariantId), quantity: chairQty },
            ],
          }),
        });

        const payload = await response.json().catch(() => null);
        if (!response.ok) {
          const message = payload?.description || payload?.message || 'Could not add bundle to cart.';
          throw new Error(message);
        }

        const cartUpdateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
        const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

        try {
          await fetch(cartUpdateUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ discount: getDiscountCode(bundle.quantity) }),
          });
        } catch (discountErr) {
          /* Items are in cart; customer can apply code manually if needed */
        }

        window.location.href = cartUrl;
      } catch (error) {
        if (customBtn) {
          customBtn.disabled = false;
          customBtn.textContent = 'Add to Cart';
        }
        window.alert(error.message || 'Something went wrong. Please try again.');
      }
    }

    // Selection changes
    addonSelect.addEventListener('change', scheduleRefresh);
    variantIdInput.addEventListener('change', scheduleRefresh);
    root.addEventListener('change', (e) => {
      if (e.target && e.target.matches('select[name^="options["], input[name^="options["]')) scheduleRefresh();
    });

    const qtyInput = quantityForm?.querySelector('input[name="quantity"]');
    if (qtyInput) {
      qtyInput.addEventListener('change', scheduleRefresh);
      qtyInput.addEventListener('input', scheduleRefresh);
    }

    // Re-apply whenever the theme re-renders the price block (variant change).
    const priceContainer = document.getElementById(`price-${sectionId}`);
    if (priceContainer && 'MutationObserver' in window) {
      new MutationObserver(() => {
        if (getBundleSelection()) scheduleRefresh();
      }).observe(priceContainer, { childList: true });
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, scheduleRefresh);
      if (PUB_SUB_EVENTS.quantityUpdate) subscribe(PUB_SUB_EVENTS.quantityUpdate, scheduleRefresh);
    }

    // Add to cart buttons
    const { customBtn } = buttonEls();
    if (customBtn) {
      customBtn.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          addBundleToCart();
        },
        true
      );
    }

    document.querySelectorAll('.cust_sticky-atc, .mob_add-to-cart').forEach((stickyBtn) => {
      stickyBtn.addEventListener('click', (e) => {
        e.preventDefault();
        if (getBundleSelection()) {
          addBundleToCart();
        } else {
          const { defaultSubmit } = buttonEls();
          if (defaultSubmit) defaultSubmit.click();
        }
      });
    });

    refreshBundlePrice();
  }

  function boot() {
    document.querySelectorAll('[data-dining-bundle]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();